/**
 * t711u: UK address lookup for the HR home address. The API proxies Google Places (the key never leaves the server)
 * and returns the place's address components; this turns them into the form's line 1, line 2, town, county and postcode.
 */
export interface AddressComponent { longText: string; shortText?: string; types: string[] }
export interface AddressParts { line1: string; line2: string; town: string; county: string; postcode: string; country: string }

const first = (cs: readonly AddressComponent[], ...types: string[]) => {
  for (const t of types) { const c = cs.find(x => x.types.includes(t)); if (c?.longText) return c.longText.trim(); }
  return '';
};

/**
 * Line 1 is the flat / building and the street ("Flat 2, Rose House, 12 High Street"); a named building with no street
 * number keeps its name on line 1 and the street on line 2. Line 2 also takes the area of a town (dependent locality).
 */
export function addressFromComponents(cs: readonly AddressComponent[]): AddressParts {
  const sub = first(cs, 'subpremise');
  const premise = first(cs, 'premise');
  const num = first(cs, 'street_number');
  const route = first(cs, 'route');
  const area = first(cs, 'sublocality_level_1', 'sublocality', 'neighborhood');
  const street = [num, route].filter(Boolean).join(' ');
  const building = [sub && /^\d/.test(sub) ? `Flat ${sub}` : sub, premise].filter(Boolean).join(', ');
  let line1: string, line2: string;
  if (building && num) { line1 = `${building}, ${street}`; line2 = area; }
  else if (building) { line1 = building; line2 = [route, area].filter(Boolean).join(', '); }
  else { line1 = street; line2 = area; }
  const town = first(cs, 'postal_town', 'locality', 'administrative_area_level_3');
  const county = first(cs, 'administrative_area_level_2');
  const postcode = first(cs, 'postal_code').toUpperCase();
  const countryCode = cs.find(x => x.types.includes('country'))?.shortText ?? '';
  const country = countryCode === 'GB' ? 'United Kingdom' : first(cs, 'country');
  return { line1, line2: line2 === town ? '' : line2, town, county: county === town ? '' : county, postcode, country };
}

/** A fresh Places session token: one per search, ended by picking a place (Google bills the pair as one session). */
export const newPlacesSession = () => (globalThis.crypto?.randomUUID?.() ?? `${Date.now().toString(36)}${Math.random().toString(36).slice(2)}`);
